import React, { Suspense, lazy, useEffect, useState } from 'react'
import { type ApplyFiltersAndDispatchParams } from '../../components/CustomTable/types'
import { CreateAndEditTerms } from './CreateAndEditTerms'
import DeleteTerms from './DeleteTerms'
import ROUTES from '../../consts/paths'
import PAGE_TITLES from '../../consts/titles'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import { useNavigate } from 'react-router-dom'
import { exportCsv } from '../../utils/exportCsv'
import { notification, closeNotification } from '../../components/Notifications'
import { notifyError } from '../../utils/notifyError'
import { Box, Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { TermsManager } from '../../APIManager'
import { useAppSearchParams } from '../../hooks/useAppSearchParams'
import { VIEW_PARAM_KEY } from './TermsTab'
import { type TableCell } from '../../models/CompletionRate'
import { formatDateByUserTimeZone } from '../../helpers/formatDateByUserTimeZone'
import { cdgCellCommonStyles } from '../../components/CustomTable/generateCells'
import { readUrlParams } from '../../utils/queryParams.ts'
import ExportProcessingModal from '../../components/ExportProcessingModal'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { ExportBackgroundTaskRunningModal } from '../../components/ExportBackgroundTaskRunningModal'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

const Terms: React.FC = () => {
  const { searchParams } = useAppSearchParams()
  const selectedView = searchParams.get(VIEW_PARAM_KEY)
  const urlParamsInJsonFormat = readUrlParams()

  // Initialize params from URL params if present, else default
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>(() => {
    const base = { pageSize: 10 }
    // If urlParamsInJsonFormat has any keys, merge them in
    if (urlParamsInJsonFormat && Object.keys(urlParamsInJsonFormat).length > 0) {
      return { ...base, ...urlParamsInJsonFormat }
    }
    return base
  })

  const navigate = useNavigate()
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportModalMessage, setExportModalMessage] = useState('')

  const {
    taskId,
    setTaskId,
    showBackgroundModal,
    setShowBackgroundModal,
    isCheckingStatus,
    checkStatusBeforeExport
  } = useTaskStatus('exportTask_terms')

  const { data: TermsList = [], isLoading: termsListLoading, isError: isTermsListError, error: termsListError } = useQuery({
    queryKey: ['getGlobalTermsListApi', params],
    queryFn: async () => (await TermsManager.getGlobalTermsListApi(params)).data,
    enabled: !!selectedView && !!params.view
  })

  useEffect(() => {
    if (isTermsListError) notifyError(termsListError, { fallbackMessage: 'Failed to load terms. Please try again.' })
  }, [isTermsListError])

  const handleEditClick = (selectedRow: any): void => {
    const termId = selectedRow?.data?.termId
    navigate(`${ROUTES.TERMS_DETAILS}/${termId}`)
  }

  const { mutate: exportTermsMutate, isPending: exportLoading } = useMutation({
    mutationFn: async () => await TermsManager.exportTermsCSV(params),
    onMutate: () => notification('Preparing download...', { variant: 'loading', persist: true }),
    onSuccess: (response, _variables, context) => {
      if (response?.status === 202) {
        closeNotification(context)
        if (response?.data?.taskId) {
          setTaskId(response.data.taskId)
        }
        setExportModalMessage(response?.data?.message || '')
        setExportModalOpen(true)
        return
      }
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers, loadingToastKey: context })
      } else {
        closeNotification(context)
      }
    },
    onError: (error: any, _variables, context) => {
      closeNotification(context)
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    },
    meta: { silent: true }
  })

  useEffect(() => {
    setParams(prev => ({
      ...prev,
      [VIEW_PARAM_KEY]: selectedView,
      search: ''
    }))
  }, [selectedView])

  const overrides = {
    startTimestamp: (cell: TableCell) => (
      <Typography sx={cdgCellCommonStyles}>{formatDateByUserTimeZone(cell.startTimestamp as string, 'MM/dd/yyyy')}</Typography>
    ),
    endTimestamp: (cell: TableCell) => (
      <Typography sx={cdgCellCommonStyles}>{formatDateByUserTimeZone(cell.endTimestamp as string, 'MM/dd/yyyy')}</Typography>
    )
  }

  const handleExport = async (): Promise<void> => {
    const canExport = await checkStatusBeforeExport()
    if (!canExport) return
    exportTermsMutate()
  }

  const topbarActionsDropdownMenu = [
    {
      text: 'Export Terms (.csv)',
      action: () => { void handleExport() },
      visible: true
    }
  ]

  return (
    <Box display='flex' flexDirection='column' gap={6}>
      <Suspense fallback={<LoadingPageV2 title={PAGE_TITLES.TERMS} />}>
        <AppTable
          key={selectedView || 'default'}
          createAction={<CreateAndEditTerms />}
          list={TermsList}
          topbarActionsDropdownMenu={topbarActionsDropdownMenu}
          exportButtonProps={{ loading: exportLoading || isCheckingStatus, disabled: exportLoading || isCheckingStatus }}
          loading={termsListLoading}
          deleteAction={<DeleteTerms />}
          onEditClick={handleEditClick}
          createActionTitle='Create Term'
          featureName="Term"
          params={params}
          setParams={setParams}
          actionModalEntityDisplayField='termName'
          overrides={overrides}
        />
      </Suspense>
      <ExportProcessingModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false) }}
        message={exportModalMessage}
      />
      <ExportBackgroundTaskRunningModal
        isOpen={showBackgroundModal}
        onClose={() => { setShowBackgroundModal(false) }}
        taskId={taskId ?? ''}
      />
    </Box>
  )
}

export default Terms
